import axios from "axios";
import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_ENV === 'production'
  ? process.env.REACT_APP_API_URL_PRODUCTION
  : process.env.REACT_APP_API_URL;

// URL del microservicio de tracking vehicular
const TRACKING_API_URL = process.env.REACT_APP_ENV === 'production'
  ? process.env.REACT_APP_TRACKING_API_URL_PRODUCTION
  : process.env.REACT_APP_TRACKING_API_URL;


// === CONFIGURACIONES BASE ===
const baseConfig = {
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  }
};

const trackingConfig = {
  baseURL: TRACKING_API_URL,
  headers: {
    "Content-Type": "application/json",
  }
};

const standardTimeout = 300000; // 5 minutos para peticiones estándar
const historyTimeout = 120000; // 2 minutos para consultas de historial

// === INSTANCIA PÚBLICA (SIN CAMBIOS) ===
export const publicApi = axios.create({
  ...baseConfig,
  timeout: standardTimeout,
});

// === FACTORY SIMPLIFICADA PARA API AUTENTICADA ===
// Ya no necesita interceptores de renovación, eso lo maneja el socket
export const createAuthenticatedApi = (token) => {
  const authenticatedApi = axios.create({
    ...baseConfig,
    timeout: standardTimeout,
    headers: {
      ...baseConfig.headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  });

  // Interceptor simple solo para manejar errores 401
  authenticatedApi.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
      }
      return Promise.reject(error);
    }
  );

  return authenticatedApi;
};

// === FACTORY PARA API DE HISTORIAL CON TIMEOUT EXTENDIDO ===
export const createHistoryApi = (token) => {
  const historyApi = axios.create({
    ...baseConfig,
    timeout: historyTimeout,
    headers: {
      ...baseConfig.headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  });

  // Interceptor específico para historial con manejo de timeouts
  historyApi.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Manejo de tokens expirados
      if (error.response?.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        return Promise.reject(error);
      }

      // Manejo específico de timeouts para historial
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        const enhancedError = {
          ...error,
          isTimeout: true,
          code: 'HISTORY_TIMEOUT',
          message: 'La consulta de historial está tomando más tiempo del esperado',
          suggestions: [
            'Reduzca el rango de fechas (máximo 24-48 horas por consulta)',
            'Especifique un límite de registros menor',
            'Intente en horarios de menor carga del servidor',
            'Use la función de consulta por lotes para períodos largos'
          ],
          originalTimeout: historyTimeout,
          recommendedAction: 'Usar consulta por chunks para rangos grandes'
        };
        
        return Promise.reject(enhancedError);
      }

      return Promise.reject(error);
    }
  );

  return historyApi;
};

// === HOOK SIMPLIFICADO ===
export const useApi = () => {
  const { token } = useAuth();
  const apiInstanceRef = useRef(null);
  
  // Crear instancia solo cuando cambia el token
  useEffect(() => {
    if (token) {
      apiInstanceRef.current = createAuthenticatedApi(token);
    } else {
      apiInstanceRef.current = null;
    }
  }, [token]);
  
  return apiInstanceRef.current || createAuthenticatedApi(token);
};

// === HOOK PARA API DE HISTORIAL ===
export const useHistoryApi = () => {
  const { useAuth } = require("../context/AuthContext");
  const { token } = useAuth();
  const historyApiInstanceRef = useRef(null);
  
  // Crear/actualizar instancia de API de historial cuando cambia el token
  useEffect(() => {
    if (token) {
      historyApiInstanceRef.current = createHistoryApi(token);
    } else {
      historyApiInstanceRef.current = null;
    }
  }, [token]);
  
  // Retornar instancia o crear una nueva si no existe
  return historyApiInstanceRef.current || createHistoryApi(token);
};

// === FACTORY PARA API DE TRACKING VEHICULAR (MICROSERVICIO) ===
export const createTrackingApi = () => {
  // USAR API KEY en lugar de token de sesión
  const trackingApiKey = process.env.REACT_APP_TRACKING_API_KEY_PRODUCTION;

  const trackingApi = axios.create({
    ...trackingConfig,
    timeout: standardTimeout,
    headers: {
      ...trackingConfig.headers,
      ...(trackingApiKey ? { 'x-api-key': trackingApiKey } : {}),
    },
  });

  // Interceptor para manejo de errores
  trackingApi.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('❌ API Key inválida o expirada para tracking');
        // No redirigir al login, es un problema de configuración
      }
      return Promise.reject(error);
    }
  );

  return trackingApi;
};

// === HOOK PARA API DE TRACKING VEHICULAR ===
export const useTrackingApi = () => {
  const trackingApiInstanceRef = useRef(null);
  
  useEffect(() => {
    // ✅ El microservicio de tracking requiere API Key
    trackingApiInstanceRef.current = createTrackingApi();
  }, []); // Sin dependencias, la API Key es estática
  
  return trackingApiInstanceRef.current || createTrackingApi();
};